import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Checkbox, Input } from "antd";
import { useTranslation } from "react-i18next";
import { Button } from "@mui/material";
import OrderForm from "./OrderForm";
import { useDispatch, useSelector } from "react-redux";
import { useMutation } from "react-query";
import { register, validateFields } from 'api'
import { subscriptionsActions } from "store/reducers/subscriptions/subscriptions";
import { showErrorMessage } from "utils/showMessage";
import CircularProgress from '@mui/material/CircularProgress';
import { VALIDATE_FIELDS } from "constant";


const SignUpPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const selectedPlan = useSelector((state) => state.subscriptions.plan) || JSON.parse(localStorage.getItem('plan'))
  const discountCode = useSelector((state) => state.subscriptions.discountCode)

  const [loading, setLoading] = useState(false)

  // Schema di validazione con Yup
  const validationSchema = Yup.object({
    email: Yup.string().email(t("Invalid email")).required(t("Required")),
    password: Yup.string()
      .min(10, t("Password must be at least 10 characters"))
      .matches(/\d/, t("Password must contain at least 1 number"))
      .matches(/[A-Z]/, t("Password must contain at least 1 uppercase letter"))
      .matches(/[!@#$%^&*(),.?":{}|<>]/, t("Password must contain 1 special character"))
      .required(t("Required")),
    password_confirmation: Yup.string()
      .oneOf([Yup.ref("password")], t("Passwords must match"))
      .required(t("Required")),
    acceptTerms: Yup.boolean().oneOf([true], t("You must accept the terms")),
  });

  // Inizializzazione di Formik
  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
      password_confirmation: "",
      acceptTerms: false,
      acceptMarketing: false,
    },
    validationSchema,
    onSubmit: (values) => {



      setLoading(true)
      validate.mutate(values.email)

    },
  });



  const registration = useMutation({
    mutationKey: 'register',
    mutationFn: (email, password, password_confirmation) => register(email, password, password_confirmation),
    onSuccess: (res) => {
      setLoading(false)
      localStorage.setItem('registrationToken', res?.resData?.data?.access_token)
      dispatch(
        subscriptionsActions.setRegistrationToken(res?.resData?.data?.access_token)
      )
      navigate('/insert-otp')

    },
    onError: (err) => {
      setLoading(false)
      const backendMessage = err?.response?.data?.errors?.[0]?.message
      if (backendMessage) {
        showErrorMessage(t(`error messages.${backendMessage}`))
      } else {
        showErrorMessage(err.message)
      }
    }
  })

  const validate = useMutation({
    mutationKey: 'register/validate-company-email',
    mutationFn: (email) => validateFields('', { field_name: VALIDATE_FIELDS.email, value: email }),
    onSuccess: (res) => {
      setLoading(false)
      if (res?.resData?.data) {

        registration.mutate({
          email: formik.values.email,
          password: formik.values.password,
          password_confirmation: formik.values.password_confirmation,
          temporary_data: JSON.stringify({
            selectedPlan: selectedPlan,
            discountCode: discountCode
          }),
          accept_terms_privacy: formik.values.acceptTerms,
          accept_marketing:     formik.values.acceptMarketing
        });
      }
    },
    onError: (err) => {
      setLoading(false)
      if (err?.response?.data?.errors[0]?.code === 1006) {
        showErrorMessage(t('error messages.Email already exists'))
      } else {
        showErrorMessage(t('error messages.Internal server error'))
      }

    }
  })

  return (
    <div className="grid grid-cols-3 h-screen w-screen bg-[#F7F7F8] p-2">
      {/* Form */}
      <div className="col-span-2 justify-left w-full h-full bg-white rounded-2xl p-[40px]">
        <p className="text-sm text-[#828282] font-normal">Step 2 {t("OF").toLowerCase()} 5</p>
        <p className="text-3xl text-black font-semibold">{t("Signup.CreateTitle")}</p>
        <p className="text-base text-[#828282] font-normal">{t("Signup.CreateSubTitle")}</p>

        <form onSubmit={formik.handleSubmit} className="mt-[40px]">
          {/* Email */}
          <p className="text-sm font-normal text-[#595961]">{t("Signup.EmailAddress")}</p>
          <Input
            type="email"
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            style={{
              width: "100%",
              border: "none",
              borderBottom: "2px solid #E3E3E8",
              borderRadius: "0px",
            }}
          />
          {formik.touched.email && formik.errors.email && (
            <p className="text-red-500 text-xs mt-1">{formik.errors.email}</p>
          )}

          {/* Password */}
          <p className="text-sm font-normal text-[#595961] mt-4">Password</p>
          <Input.Password
            name="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            style={{
              width: "100%",
              border: "none",
              borderBottom: "2px solid #E3E3E8",
              borderRadius: "0px",
            }}
          />
          {formik.touched.password && formik.errors.password && (
            <p className="text-red-500 text-xs mt-1">{formik.errors.password}</p>
          )}

          <p className='text-xs font-normal text-[#595961] mt-2'>{t("Signup.PasswordText")}</p>

          {/* Conferma Password */}
          <p className="text-sm font-normal text-[#595961] mt-4">{t("Signup.RepeatPsw")}</p>
          <Input.Password
            name="password_confirmation"
            value={formik.values.password_confirmation}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            style={{
              width: "100%",
              border: "none",
              borderBottom: "2px solid #E3E3E8",
              borderRadius: "0px",
            }}
          />
          {formik.touched.password_confirmation && formik.errors.password_confirmation && (
            <p className="text-red-500 text-xs mt-1">{formik.errors.password_confirmation}</p>
          )}

          {/* Checkbox */}
          <Checkbox
            className="text-xs font-normal text-[#828282] mt-8"
            name="acceptTerms"
            checked={formik.values.acceptTerms}
            onChange={formik.handleChange}
          >
            {t("Signup.AcceptTerms")}
            <br />
            <span className="text-[#828282] text-xs !text-[10px]">
              {t("Signup.FieldRequired")}</span>
          </Checkbox>
          {formik.touched.acceptTerms && formik.errors.acceptTerms && (
            <p className="text-red-500 text-xs mt-1">{formik.errors.acceptTerms}</p>
          )}

          <Checkbox
            className="text-xs font-normal text-[#828282] mt-2"
            name="acceptMarketing"
            checked={formik.values.acceptMarketing}
            onChange={formik.handleChange}
          >
            {t("Signup.AcceptMarketing")}
          </Checkbox>

          {/* Pulsanti */}
          <div className="mt-[58px] flex flex-col items-center">
            <div>
              
              <Button
                onClick={() => {
                  navigate(-1)
                }}
                type="button"
                variant="outlined"
                sx={{
                  width: 124,
                  borderColor: "#919191",
                  fontSize: "16px",
                  textTransform: "none",
                  color: "#919191",
                  paddingLeft: "22px",
                  paddingRight: "22px",
                  fontWeight: 600,
                  marginRight: 2,
                }}
              >
                {t("Signup.Back")}
              </Button>
              
              <Button
                type="submit"
                variant="contained"
                disabled={!formik.isValid || !formik.dirty || loading}
                sx={{
                  width: 124,
                  paddingLeft: "22px",
                  paddingRight: "22px",
                  fontWeight: 600,
                  backgroundColor: formik.isValid ? "#0049EE" : "#A0A0A0",
                  fontSize: "16px",
                  textTransform: "none",
                }}
              >
                {t("Signup.Forward")}
                {loading && (<CircularProgress size="20px" style={{ marginLeft: 8, color: 'white' }} />)}
              
              </Button>
            </div>
            <div>
              <p className="text-[16px] font-normal text-[#595961] mt-4">
                {t("Signup.AlreadyHaveAccount")}
                <span
                  className="text-[#0049EE] cursor-pointer font-[600]"
                  onClick={() => navigate("/login")}
                >
                  {' ' + t("Signup.SignIn")}
                </span>
              </p>
            </div>
          </div>
        </form>
      </div>

      {/* Sidebar con dettagli */}
      <OrderForm showDiscount={false} />
    </div>
  );
};

export default SignUpPage;
